import {
  ActionType,
  filterCatalogActionsForRobotType,
  isDrainOnlyMaintenancePointType,
  isSweeperRobotType,
  type ILotModelConfig,
} from 'utils/servicePointsCatalog'
import { isWashingLotCompatibleWithRobotType } from 'utils/stationLotModel'

import type { MaintenancePointKind, ServiceCalibCatalogData, ServiceCalibCatalogItem } from './serviceCalibTypes'
import {
  ACTION_CHARGING,
  ACTION_DEBRIS_DUMP,
  ACTION_PARKING,
  ACTION_REFILLING,
  ACTION_WASHING,
  ACTION_WASTE_WATER_DRAIN,
} from './serviceCalibUtils'

export { filterCatalogActionsForRobotType, isSweeperRobotType }

export const MAINTENANCE_POINT_ALLOWED_ACTIONS: Record<MaintenancePointKind, ActionType[]> = {
  waste_water_drain: [ActionType.WASTE_WATER_DRAIN],
  debris_dump: [ActionType.DEBRIS_DUMP],
  universal: [ActionType.WASTE_WATER_DRAIN, ActionType.DEBRIS_DUMP],
}

const ACTION_CODE_TO_TYPE: Record<number, ActionType> = {
  [ACTION_PARKING]: ActionType.PARKING,
  [ACTION_WASHING]: ActionType.WASHING,
  [ACTION_CHARGING]: ActionType.CHARGING,
  [ACTION_REFILLING]: ActionType.REFILLING,
  [ACTION_WASTE_WATER_DRAIN]: ActionType.WASTE_WATER_DRAIN,
  [ACTION_DEBRIS_DUMP]: ActionType.DEBRIS_DUMP,
}

export function getCatalogItemAllowedActions(
  item: ServiceCalibCatalogItem,
  lotModels: ILotModelConfig[],
  robotType?: string | null
): ActionType[] {
  let allowed: ActionType[] = []
  if (item.target === 'station_lot') {
    const lotModel = item.metadata.lot_model?.trim()
    const config = lotModels.find(model => model.model_number === lotModel)
    if (config?.allowed_actions?.length) {
      allowed = [...config.allowed_actions]
    }
  } else if (item.maintenancePointType) {
    allowed = [...(MAINTENANCE_POINT_ALLOWED_ACTIONS[item.maintenancePointType] ?? [])]
  }
  return filterCatalogActionsForRobotType(allowed, robotType)
}

export function isDrainOnlyMaintenancePoint(item: ServiceCalibCatalogItem): boolean {
  return item.target === 'maintenance_point' && isDrainOnlyMaintenancePointType(item.maintenancePointType)
}

function calibRobotTypeCode(robotType?: string | null): string | undefined {
  const code = (robotType || '').trim()
  if (!code || code === 'Unknown') return undefined
  return code
}

function isCalibCatalogItemVisible(item: ServiceCalibCatalogItem, robotType?: string | null): boolean {
  if (isSweeperRobotType(robotType) && isDrainOnlyMaintenancePoint(item)) {
    return false
  }
  if (item.target !== 'station_lot') {
    return true
  }
  return isWashingLotCompatibleWithRobotType(item.metadata.lot_model, calibRobotTypeCode(robotType))
}

export function filterServiceCalibCatalogForRobotType(
  catalog: ServiceCalibCatalogData,
  robotType?: string | null
): ServiceCalibCatalogData {
  const keep = (item: ServiceCalibCatalogItem) => isCalibCatalogItemVisible(item, robotType)
  const stationGroups = catalog.stationGroups
    .map(group => ({ ...group, lots: group.lots.filter(keep) }))
    .filter(group => group.lots.length > 0)
  const maintenancePoints = catalog.maintenancePoints.filter(keep)
  const mapItems = catalog.mapItems.filter(keep)
  const groupsUnchanged =
    stationGroups.length === catalog.stationGroups.length &&
    stationGroups.every((group, index) => group.lots.length === catalog.stationGroups[index].lots.length)
  if (
    groupsUnchanged &&
    maintenancePoints.length === catalog.maintenancePoints.length &&
    mapItems.length === catalog.mapItems.length
  ) {
    return catalog
  }
  return {
    ...catalog,
    stationGroups,
    maintenancePoints,
    mapItems,
  }
}

export function resolveSelectedCatalogActions(
  item: ServiceCalibCatalogItem,
  selectedByKey: Record<string, ActionType[]>,
  lotModels: ILotModelConfig[],
  robotType?: string | null
): ActionType[] {
  const allowed = getCatalogItemAllowedActions(item, lotModels, robotType)
  if (allowed.length <= 1) {
    return allowed
  }
  const selected = selectedByKey[item.key] ?? []
  return selected.filter(action => allowed.includes(action))
}

export function needsCatalogActionSelection(
  item: ServiceCalibCatalogItem,
  lotModels: ILotModelConfig[],
  selectedByKey: Record<string, ActionType[]>,
  robotType?: string | null
): boolean {
  const allowed = getCatalogItemAllowedActions(item, lotModels, robotType)
  if (allowed.length <= 1) {
    return false
  }
  return resolveSelectedCatalogActions(item, selectedByKey, lotModels, robotType).length === 0
}

export function actionTypeToRosCode(action: ActionType): number | null {
  switch (action) {
    case ActionType.PARKING:
      return ACTION_PARKING
    case ActionType.WASHING:
      return ACTION_WASHING
    case ActionType.CHARGING:
      return ACTION_CHARGING
    case ActionType.REFILLING:
      return ACTION_REFILLING
    case ActionType.WASTE_WATER_DRAIN:
      return ACTION_WASTE_WATER_DRAIN
    case ActionType.DEBRIS_DUMP:
      return ACTION_DEBRIS_DUMP
    default:
      return null
  }
}

export function rosCodeToActionType(code: number): ActionType | null {
  return ACTION_CODE_TO_TYPE[code] ?? null
}

/** Map ROS action_type[] from active_mode_metadata / outgoing command into UI ActionType[]. */
export function actionTypesFromRosCodes(codes: number[] | null | undefined): ActionType[] {
  if (!codes?.length) {
    return []
  }
  const actions: ActionType[] = []
  for (const code of codes) {
    const action = rosCodeToActionType(Number(code))
    if (action != null && !actions.includes(action)) {
      actions.push(action)
    }
  }
  return actions
}
